import { useLogger } from "amazon-chime-sdk-component-library-react";
import { useDevices } from "../../../providers/DevicesProvider";
import DeviceInput from "../DeviceInput";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";
import { isContinuityMicLabel } from "../../../utils/DeviceManager";
import { usePlatformStore } from "@platform/store";

interface Props {
  /** The message that will be shown when no microphone devices are found. */
  notFoundMsg?: string;
  /** The label that will be shown for microphone selection, it defaults to `Microphone source`. */
  label?: string;
}

export const MicSelection: React.FC<React.PropsWithChildren<Props>> = ({
  notFoundMsg = "No microphone devices found",
  label = "Microphone source",
  ...rest
}) => {
  const logger = useLogger();
  const {
    audioInputDevices,
    selectedAudioInputDevice,
    selectAudioInputDevice,
  } = useDevices();

  const { supportsAudio } = useBrowserCompatibilityFeatures();
  const setUserExplicitlyChoseIPhoneMic = usePlatformStore(
    (s) => s.setUserExplicitlyChoseIPhoneMic,
  );

  const handleSelect = async (deviceId: string): Promise<void> => {
    try {
      // Symmetric flag set/clear: a user-pick of any Continuity-rule device
      // signals intent; a user-pick of any non-Continuity device signals the
      // intent has moved away. Either way the iPhone-mic alert re-evaluates.
      const pickedLabel =
        audioInputDevices.find((d) => d.deviceId === deviceId)?.label ?? "";
      setUserExplicitlyChoseIPhoneMic(isContinuityMicLabel(pickedLabel));

      await selectAudioInputDevice(deviceId);
    } catch (error) {
      logger.error("MicSelection failed to select mic");
    }
  };

  return (
    <DeviceInput
      name="mic-selection"
      label={label}
      onChange={handleSelect}
      devices={audioInputDevices}
      selectedDevice={selectedAudioInputDevice}
      notFoundMsg={notFoundMsg}
      disabled={!supportsAudio}
      {...rest}
    />
  );
};

export default MicSelection;
